import { Button } from '@quadralize/ui'
import { Home } from '@solar-icons/react'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

interface ErrorLayoutProps {
  code: string | number
  title: string
  description: string
  icon: React.ReactNode
  blobs?: Array<{
    id: string
    color: string
    size: string
    top?: string
    left?: string
    right?: string
    bottom?: string
    delay: number
  }>
  showHomeButton?: boolean
  actions?: React.ReactNode
  errorCode?: string
}

const ANIMATION_CONFIG = {
  MOUSE_RANGE: 200,
  ROTATE_X_MAX: 5,
  ROTATE_Y_MAX: 5,
  BLOB_Y_RANGE: 20,
  BLOB_X_RANGE: 15,
  BLOB_SCALE: 1.1,
  BLOB_DURATION: 10,
  RING_DUR_BASE: 15,
  RING_DUR_STEP: 5,
  RING_SIZE_BASE: 50,
  GRID_SIZE: 32,
  FULL_ROTATE: 360,
  SCALE_PULSE: 1.05,
  PULSE_DURATION: 4,
  RING_SCALES: {
    SMALL: 1.5,
    MEDIUM: 2.2,
    LARGE: 3,
  },
} as const

export function ErrorLayout({
  code,
  title,
  description,
  icon,
  blobs = [
    {
      id: 'blob-1',
      color: 'bg-primary/20',
      size: 'w-48 h-48 md:w-80 md:h-80',
      top: '-5%',
      left: '-5%',
      delay: 0,
    },
    {
      id: 'blob-2',
      color: 'bg-indigo-500/15',
      size: 'w-56 h-56 md:w-96 md:h-96',
      bottom: '-10%',
      right: '-5%',
      delay: 2,
    },
    {
      id: 'blob-3',
      color: 'bg-rose-500/10',
      size: 'w-40 h-40 md:w-64 md:h-64',
      top: '30%',
      right: '10%',
      delay: 4,
    },
  ],
  showHomeButton = true,
  actions,
  errorCode,
}: ErrorLayoutProps) {
  const navigate = useNavigate()
  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const mouseX = useSpring(x, { stiffness: 100, damping: 20 })
  const mouseY = useSpring(y, { stiffness: 100, damping: 20 })

  const rotateX = useTransform(
    mouseY,
    [-ANIMATION_CONFIG.MOUSE_RANGE, ANIMATION_CONFIG.MOUSE_RANGE],
    [ANIMATION_CONFIG.ROTATE_X_MAX, -ANIMATION_CONFIG.ROTATE_X_MAX],
  )
  const rotateY = useTransform(
    mouseX,
    [-ANIMATION_CONFIG.MOUSE_RANGE, ANIMATION_CONFIG.MOUSE_RANGE],
    [-ANIMATION_CONFIG.ROTATE_Y_MAX, ANIMATION_CONFIG.ROTATE_Y_MAX],
  )

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-background px-4 py-8 md:py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {blobs.map((blob) => (
          <motion.div
            key={blob.id}
            className={`absolute rounded-full blur-3xl ${blob.color} ${blob.size}`}
            style={{
              top: blob.top,
              left: blob.left,
              right: blob.right,
              bottom: blob.bottom,
            }}
            animate={{
              y: [0, ANIMATION_CONFIG.BLOB_Y_RANGE, 0],
              x: [0, ANIMATION_CONFIG.BLOB_X_RANGE, 0],
              scale: [1, ANIMATION_CONFIG.BLOB_SCALE, 1],
            }}
            transition={{
              duration: ANIMATION_CONFIG.BLOB_DURATION,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: blob.delay,
            }}
          />
        ))}

        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(to right, currentColor 1px, transparent 1px)',
            backgroundSize: `${ANIMATION_CONFIG.GRID_SIZE}px ${ANIMATION_CONFIG.GRID_SIZE}px`,
          }}
        />
      </div>

      <div className="container relative z-10 mx-auto max-w-[90vw]">
        <motion.div
          style={{ rotateX, rotateY, perspective: 1000 }}
          className="flex flex-col items-center"
        >
          <div className="relative mb-8 md:mb-16 flex items-center justify-center scale-90 md:scale-100">
            <motion.h1
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 0.07, scale: 1 }}
              transition={{ duration: 1.5 }}
              className="absolute text-[8rem] sm:text-[15rem] md:text-[25rem] font-black text-foreground pointer-events-none select-none -z-10 tracking-tighter"
            >
              {code}
            </motion.h1>

            <div className="relative z-10 flex flex-col items-center">
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="relative"
              >
                {[
                  { id: 'ring-1', scale: ANIMATION_CONFIG.RING_SCALES.SMALL },
                  { id: 'ring-2', scale: ANIMATION_CONFIG.RING_SCALES.MEDIUM },
                  { id: 'ring-3', scale: ANIMATION_CONFIG.RING_SCALES.LARGE },
                ].map((ring, idx) => (
                  <motion.div
                    key={ring.id}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border border-primary/20 rounded-full pointer-events-none"
                    style={{
                      width: `${ring.scale * ANIMATION_CONFIG.RING_SIZE_BASE}px`,
                      height: `${ring.scale * ANIMATION_CONFIG.RING_SIZE_BASE}px`,
                    }}
                    animate={{
                      rotate:
                        idx % 2 === 0
                          ? ANIMATION_CONFIG.FULL_ROTATE
                          : -ANIMATION_CONFIG.FULL_ROTATE,
                      scale: [1, ANIMATION_CONFIG.SCALE_PULSE, 1],
                    }}
                    transition={{
                      rotate: {
                        duration:
                          ANIMATION_CONFIG.RING_DUR_BASE + idx * ANIMATION_CONFIG.RING_DUR_STEP,
                        repeat: Infinity,
                        ease: 'linear',
                      },
                      scale: {
                        duration: ANIMATION_CONFIG.PULSE_DURATION,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      },
                    }}
                  />
                ))}

                <div className="relative p-6 md:p-8 bg-background/40 backdrop-blur-md border border-border/50 rounded-4xl md:rounded-[2.5rem] shadow-xl flex items-center justify-center">
                  <div className="absolute -top-1 -left-1 w-3 h-3 md:w-4 md:h-4 border-t-2 border-l-2 border-primary rounded-tl-lg" />
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 md:w-4 md:h-4 border-b-2 border-r-2 border-primary rounded-br-lg" />

                  <div className="text-primary w-12 h-12 md:w-16 md:h-16 [&>svg]:w-full [&>svg]:h-full">
                    {icon}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          <div className="max-w-xl text-center px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6 tracking-tight bg-linear-to-b from-foreground to-foreground/60 bg-clip-text text-transparent">
                {title}
              </h2>

              <p className="text-base md:text-xl text-muted-foreground mb-8 md:mb-12 leading-relaxed">
                {description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              {actions}

              {showHomeButton && (
                <Button
                  size="lg"
                  startIcon={<Home className="w-5 h-5" />}
                  onClick={() => navigate('/')}
                  variant={actions ? 'outline' : 'default'}
                >
                  Voltar ao início
                </Button>
              )}
            </motion.div>

            {errorCode && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                transition={{ delay: 1, duration: 1 }}
                className="mt-10 md:mt-16 pt-6 md:pt-8 border-t border-border/20"
              >
                <div className="flex justify-center items-center gap-3 text-xs md:text-sm font-medium text-muted-foreground uppercase tracking-widest">
                  <span className="w-6 md:w-8 h-px bg-border/40" />
                  Código do erro: {errorCode}
                  <span className="w-6 md:w-8 h-px bg-border/40" />
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}
